import SpineWrapper from '@Core/Animation/SpineWrapper';
import { GameScene } from '@Core/GameScene/GameScene';
import Symbol from '@Core/Reels/Symbol';
import { ESymbolAnimations } from '@Core/Reels/SymbolAnimations';
import { ESymbolRoles } from '@Core/Reels/SymbolRoles';
import { SymbolSpinePool } from '@Core/SpinePool/SymbolSpinePool';
import { CycleStep } from '@Core/SymbolsHighlighter/SymbolsHighlighter.types';
import { getEventWithoutParams } from '@Core/Utils/ModifierUtils';
import { getSymbolRoles } from '@Core/Utils/SymbolUtils';
import { GameStore } from '@GameStore/GameStore';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { ISpineConfig } from '@GFTypes/Spines';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { DeferredPromise } from '@Tools/DeferredPromise';
import { EExpandingSymbolAnimationModifiers, EExpandingSymbolAnimations } from './ExpandingSymbolAnimations';
import { IPositionOnReels, SymbolExpansionStrategy } from './ExpandingSymbolFeature.types';
import { Container, Point } from 'pixi.js';
import { Animation } from '@esotericsoftware/spine-pixi-v8';

export class ExpandingSymbol {
    public spine: SpineWrapper;
    public shouldExpand: boolean;
    public expanded: boolean;
    private readonly eventSubscriptions: { event: string | symbol; fn: (...args: any[]) => void; context?: any }[] = [];
    public readonly animationVariants: Map<EExpandingSymbolAnimations, Map<Set<string>, string[]>> = new Map();
    public winningExpandingSymbolIndex: number;
    public isLastWinningExpandingSymbol = false;
    public visibleRowIndex: number;
    private readonly skipPromise: DeferredPromise;
    private symbolSpineConfig: ISpineConfig;
    private readonly gameStore: GameStore;

    constructor(
        public id: number,
        public readonly originPositions: IPositionOnReels[],
        public readonly coveredPositions: IPositionOnReels[],
        public readonly showOnlyHighestMultiplier: boolean,
        private readonly scene: GameScene,
        private readonly container: Container,
        private readonly expansionStrategy: SymbolExpansionStrategy,
        private readonly isVisibleAfterExpanding: boolean = true,
        private readonly shouldHideRightAfterExpand: boolean = true
    ) {
        const symbolSpineConfig = this.scene.game.gameStore.get(EGameStoreKeys.SymbolConfiguration).find((symbol) => symbol.id === this.id)!;
        this.spine = new SpineWrapper(scene, { x: 430, y: 430 }, { ...symbolSpineConfig.asset, atlasKey: undefined, manuallyScaleOnResize: true }, false);
        this.animationVariants = this.buildAnimationIndex();
        this.container.addChild(this.spine.spine);
        this.setVisible(false);
        this.eventSubscriptions.push({ event: EGameEvents.RESIZE_SYMBOLS, fn: this.onSymbolsResize, context: this });
        this.winningExpandingSymbolIndex = 0;
        this.skipPromise = new DeferredPromise();
        this.gameStore = this.scene.game.gameStore;
    }

    private buildAnimationIndex(): Map<EExpandingSymbolAnimations, Map<Set<string>, string[]>> {
        const result = new Map<EExpandingSymbolAnimations, Map<Set<string>, string[]>>();
        const allAnimations = this.spine.spine.skeleton.data.animations.map((animation) => animation.name);
        const processedAnimations = new Set<string>();

        for (const base of Object.values(EExpandingSymbolAnimations).toSorted((nameA, nameB) => nameB.length - nameA.length)) {
            const map = new Map<Set<string>, string[]>();

            allAnimations
                .filter((animation) => animation.includes(base))
                .forEach((animation) => {
                    if (processedAnimations.has(animation)) return;
                    const mods: string[] = [];

                    for (const modifier of Object.values(EExpandingSymbolAnimationModifiers)) {
                        if (modifier.includes('$')) {
                            const regex = new RegExp(modifier.replaceAll('$', String.raw`(\d+)`));
                            const match = regex.exec(animation);

                            if (match) {
                                let groupIndex = 0;

                                const filledModifier = modifier.replaceAll('$', () => {
                                    groupIndex++;
                                    return match[groupIndex];
                                });

                                mods.push(filledModifier);
                            }
                        } else if (animation.includes(modifier)) {
                            mods.push(modifier);
                        }
                    }
                    if (mods.length === 0) return;
                    const key = new Set(mods);
                    const value = map.get(key) ?? [];
                    value.push(animation);
                    map.set(key, value);
                    processedAnimations.add(animation);
                });

            result.set(base, map);
        }
        return result;
    }

    public getExpandingAnimationName(): string | undefined {
        return this.getAnimationVariant(this.expansionStrategy.getExpandingAnimationName(this));
    }

    public async expand(skip: boolean = false) {
        this.setVisible(true);
        this.onSymbolsResize();
        const animationName = this.getExpandingAnimationName();
        if (!animationName) return;
        this.spine.play(animationName);
        this.skipPromise.reset();
        const { promise, cancel } = this.spine.waitForAnimationEvent(getEventWithoutParams(animationName), 'complete');
        if (skip) this.skip();
        await Promise.race([promise, this.skipPromise.promise]);
        cancel();

        this.changeCoveredSymbols(this.coveredPositions);
        this.setBlockSymbols(true, this.coveredPositions, this.isVisibleAfterExpanding);
        if (this.isVisibleAfterExpanding) {
            this.shouldExpand = false;
            this.expanded = true;
        } else if (this.shouldHideRightAfterExpand) {
            this.hide();
        }
    }

    public hide() {
        this.setVisible(false);
        this.expanded = false;
        this.setBlockSymbols(false, this.coveredPositions, this.isVisibleAfterExpanding);
    }

    public assignEventSubscriptions(...subscriptions: { event: string | symbol; fn: (...args: any[]) => void; context?: any }[]) {
        this.eventSubscriptions.push(...subscriptions);
    }

    private onSymbolsResize() {
        const visibleSymbols = this.gameStore.get(EGameStoreKeys.VisibleSymbols);
        const point = this.expansionStrategy.getSpinePosition(this, visibleSymbols);
        const firstCoveredSymbol = visibleSymbols[this.coveredPositions[0].reelIndex][this.coveredPositions[0].visibleSymbolIndex];
        const globalPoint = firstCoveredSymbol.parent!.toGlobal(point);
        const localPoint = this.spine.spine.parent!.toLocal(globalPoint);
        this.spine.spine.scale = firstCoveredSymbol.scale;
        this.spine.spine.position.set(localPoint.x, localPoint.y);
    }

    private changeCoveredSymbols(positions: IPositionOnReels[]): void {
        const visibleSymbols = this.gameStore.get(EGameStoreKeys.VisibleSymbols);
        for (const position of positions) {
            const visibleSymbol = visibleSymbols[position.reelIndex][position.visibleSymbolIndex];

            const symbolSpineConfig = this.gameStore.get(EGameStoreKeys.SymbolConfiguration).find((symbol) => symbol.id === this.id);
            if (!symbolSpineConfig) return;
            visibleSymbol.changeSymbol(
                this.id,
                symbolSpineConfig?.asset,
                position.reelIndex,
                position.visibleSymbolIndex + 1,
                this.scene.reels.symbolSpinePool
            );
        }
    }

    private setBlockSymbols(block: boolean, positions: IPositionOnReels[], isVisibleAfterExpanding: boolean): void {
        const visibleSymbols = this.gameStore.get(EGameStoreKeys.VisibleSymbols);
        for (const position of positions) {
            const visibleSymbol = visibleSymbols[position.reelIndex][position.visibleSymbolIndex];
            if (isVisibleAfterExpanding) visibleSymbol.visible = !block;
            if (block) visibleSymbol.block();
            else visibleSymbol.unBlock();
        }
    }

    private setVisible(visible: boolean): void {
        for (const { event, fn, context } of this.eventSubscriptions) {
            if (visible) EventsCenter.on(event, fn, context);
            else EventsCenter.off(event, fn, context);
        }
        this.spine.spine.visible = visible;
        if (!visible) {
            this.spine.spine.state.clearTracks();
            this.spine.spine.skeleton.setupPose();
            this.spine.forceUpdate();
        }
    }

    public startWinAnimationIfWinning({ selectionMask }: CycleStep): void {
        const shouldPlay = selectionMask
            .flatMap((reel, reelIndex) =>
                reel.map((isWinning, visibleSymbolIndex) => ({ reelIndex, visibleSymbolIndex, isWinning })).filter((pos) => pos.isWinning)
            )
            .some((pos) =>
                this.coveredPositions.some((covered) => covered.reelIndex === pos.reelIndex && covered.visibleSymbolIndex === pos.visibleSymbolIndex)
            );

        if (shouldPlay) {
            const animationName = this.expansionStrategy.getAnimationVariant(EExpandingSymbolAnimations.SYMBOL_EXPANDING_WIN, this);
            this.spine.play(animationName);
        }
    }

    public startWinAnimation(loop = false): void {
        const animationName = this.expansionStrategy.getAnimationVariant(EExpandingSymbolAnimations.SYMBOL_EXPANDING_WIN, this);
        this.spine.play(animationName, loop);
    }

    public skip() {
        this.skipPromise.resolve();
        const track = this.spine.getCurrentAnimationTrack();
        if (track?.animation?.name && track?.animation?.name === this.getExpandingAnimationName()) {
            track.trackTime = track.animation.duration;
            this.spine.forceUpdate();
        }
    }

    public changeSymbol(symbolId: number, pool: SymbolSpinePool): void {
        const symbolSpineConfig = this.gameStore.get(EGameStoreKeys.SymbolConfiguration).find((symbol) => symbol.id === symbolId)?.asset;
        if (!symbolSpineConfig) return;
        this.spine.eventManager.unregisterBlocker(Symbol.BLOCKER_NAME);
        this.container.removeChild(this.spine.spine);
        if (this.symbolSpineConfig?.dataKey) pool.releaseSpine(this.symbolSpineConfig.dataKey, this.spine);
        this.id = symbolId;
        this.symbolSpineConfig = symbolSpineConfig;
        this.spine = pool.getSpine(symbolSpineConfig.dataKey);
        if (symbolSpineConfig.skinName) this.spine.spine.skeleton.setSkin(symbolSpineConfig.skinName);
        this.spine.spine.position = new Point(0, 0);
        this.spine.spine.scale = 1;
        this.spine.spine.visible = false;
        this.spine.play(ESymbolAnimations.DEFAULT, false);
        this.container.addChild(this.spine.spine);
        this.spine.forceUpdate();
    }

    public getAnimationVariant(animation: string): string | undefined {
        const animations = this.spine.spine.skeleton.data.animations;
        const animationsForEvent = animations
            .filter((_animation: Animation) => getEventWithoutParams(_animation.name) === getEventWithoutParams(animation))
            .map((animation) => animation.name);
        const specialAnimation = animationsForEvent.find((name) => name.includes('SPECIAL/'));
        if (getSymbolRoles(this.gameStore, this.id).includes(ESymbolRoles.SPECIAL_SYMBOL) && specialAnimation) return specialAnimation;
        return animationsForEvent[0];
    }
}
